import React, { useEffect, useRef, useState } from 'react';
import { MediaRenderer } from '../media-renderer';
import { Box } from '@/shared/ui/box';

interface LazyMediaProps {
  videoSrc?: string;
  imageSrc?: string;
  alt?: string;
  mobileLandscape?: boolean;
}

export const LazyMedia: React.FC<LazyMediaProps> = ({
  videoSrc,
  imageSrc,
  alt,
  mobileLandscape,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Обработчик ошибки видео
  const handleVideoError = () => {
    setHasVideoError(true);
    setShowVideo(false);
  };

  // Отслеживаем появление в области видимости
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      {
        rootMargin: '200px',
        threshold: 0.01,
      }
    );

    const currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [isVisible]);

  // Когда элемент становится видимым, начинаем загрузку
  useEffect(() => {
    if (isVisible && !isLoaded) {
      setIsLoaded(true);
    }
  }, [isVisible, isLoaded]);

  // При загрузке: если есть видео - пробуем загрузить его
  useEffect(() => {
    if (isLoaded && videoSrc) {
      setShowVideo(true);
    }
  }, [isLoaded, videoSrc]);

  return (
    <Box ref={containerRef} $width="100%" $height="100%">
      <MediaRenderer
        videoSrc={videoSrc}
        imageSrc={imageSrc}
        alt={alt}
        showVideo={showVideo}
        hasVideoError={hasVideoError}
        isLoaded={isLoaded}
        onVideoError={handleVideoError}
      />
    </Box>
  );
};
